import { useCallback, useEffect, useState } from "react";
import { useIntl, type IntlShape } from "react-intl";
import { Button } from "@/components/design/Button";
import { FilterChip } from "@/components/design/FilterChip";
import { Switch } from "@/components/design/Switch";
import { FileDropZone } from "@/components/common/FileDropZone";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { SettingsCard } from "./SettingsCard";
import {
  type AdminDocument,
  cancelReprocess,
  deleteDocument,
  downloadOriginal,
  listDocuments,
  patchDocument,
  reprocessDocument,
  uploadDocument,
} from "@/lib/documents";
import { supportsFigures } from "@/lib/figure-support";
import { RAG_DOCUMENT_ACCEPT } from "@/lib/upload-limits";
import { withinLoadingWindow } from "@/lib/document-status";

const SECTIONS = ["SAVE", "GROWTH", "SHARE", "MATCH"] as const;
// agent-service's Section enum (Section.kt) names this area GROWTH; the app itself calls it
// "Grow" everywhere a user sees it. Label the raw value instead of renaming it — the string goes
// straight to the backend via documents.ts (form.append("sections", s)).
const SECTION_LABELS: Record<string, string> = { GROWTH: "GROW" };
const PROVIDER_LABELS: Record<string, string> = { mistral: "Mistral", openai: "OpenAI", anthropic: "Anthropic" };
const INPUT = "smycles-field-value w-full rounded bg-surface px-3 py-2.5 ring-1 ring-ink/10 focus:outline-none focus-visible:ring-1 focus-visible:ring-ink-ui";

// A doc still pending/processing past the loading window means the worker never finished — almost always
// the system was shut down mid-ingest. Past the cutoff we stop the spinner + polling and re-enable
// Reprocess so the admin can recover it, rather than spinning forever against a dead pipeline. The window
// rule lives in lib/document-status (shared with the chat attach chips).
const isLoading = (doc: AdminDocument) =>
  (doc.status === "pending" || doc.status === "processing") &&
  // Anchor on updatedAt (bumped on claim/persist/requeue), not createdAt, so a reprocessed OLD document
  // still animates. Falls back to createdAt if the field is absent (older backend).
  withinLoadingWindow(new Date(doc.updatedAt ?? doc.createdAt).getTime());

// Knowledge documents (Settings · AI Model, spec §6/§11): admins upload/manage the per-system RAG
// documents that back the assistant's per-document search tools. Uploaded docs flow through the async
// pipeline (pending → processing → ready|failed); status is poll-driven (no SSE). Global docs are
// Smycles-shipped: read-only except the per-system enable toggle.
export function KnowledgeDocumentsCard({ provider, refreshKey, uploadBlockedUntil }: { provider: string; refreshKey?: number; uploadBlockedUntil?: number }) {
  const intl = useIntl();
  const [docs, setDocs] = useState<AdminDocument[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sections, setSections] = useState<string[]>([...SECTIONS]);
  const [textOnly, setTextOnly] = useState(false);
  const [blockRemaining, setBlockRemaining] = useState(0); // seconds left on the post-switch upload block

  const reload = useCallback(async (signal?: AbortSignal) => {
    try {
      setDocs(await listDocuments(signal));
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    void reload(ac.signal);
    return () => ac.abort();
  }, [reload]);

  // Poll while anything is actively loading — the pipeline runs off-request, so the list is how the UI
  // learns a document turned `ready`/`failed` (spec §6). `isLoading` caps this at LOADING_TIMEOUT_MS so a
  // doc stuck `processing` after a shutdown stops polling. Re-provider ({provider}) reloads availability.
  const inFlight = docs.some(isLoading);
  useEffect(() => {
    if (!inFlight) return;
    const t = window.setInterval(() => void reload(), 4000);
    return () => window.clearInterval(t);
  }, [inFlight, reload]);

  // `refreshKey` bumps after a provider Save (ModelPanel) so availability re-derives against the new
  // active provider once the gateway picks it up (~30s; the save toast sets that expectation).
  useEffect(() => {
    void reload();
  }, [provider, refreshKey, reload]);

  // After a provider switch the upload button is blocked until the gateway picks the change up (~30s),
  // so a new document isn't ingested against the OLD provider. Date.now() stays out of render (purity):
  // the countdown lives in state, refreshed each second until it hits 0, when the button re-enables.
  useEffect(() => {
    if (!uploadBlockedUntil) {
      setBlockRemaining(0);
      return;
    }
    const update = () => setBlockRemaining(Math.max(0, Math.ceil((uploadBlockedUntil - Date.now()) / 1000)));
    update();
    const id = window.setInterval(() => {
      update();
      if (Date.now() >= uploadBlockedUntil) window.clearInterval(id);
    }, 1000);
    return () => window.clearInterval(id);
  }, [uploadBlockedUntil]);
  const uploadBlocked = blockRemaining > 0;

  // Figures (images) are only extracted for pdf/docx/pptx; any other format is ingested text-only. For
  // those the toggle is forced on and locked (the backend enforces this too) — derived, so no effect needed.
  const figuresSupported = file ? supportsFigures(file.name, file.type) : true;
  const effectiveTextOnly = textOnly || !figuresSupported;

  const canUpload = !busy && file !== null && title.trim().length > 0 && sections.length > 0 && !uploadBlocked;
  // Always explain a disabled Upload button (spec: no silent dead controls). Priority: the post-switch
  // block, then any recent action notice, then the first unmet requirement.
  const uploadHint = uploadBlocked
    ? null // while blocked the countdown lives on the button itself
    : notice
      ? notice
      : file === null
        ? intl.formatMessage({ id: "model.docs.needFile", defaultMessage: "Choose a document to upload." })
        : title.trim().length === 0
          ? intl.formatMessage({ id: "model.docs.needTitle", defaultMessage: "Add a title." })
          : sections.length === 0
            ? intl.formatMessage({ id: "model.docs.needSections", defaultMessage: "Select at least one section." })
            : null;
  const toggleUploadSection = (s: string) =>
    setSections((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const onUpload = async () => {
    if (!file) return;
    setBusy(true);
    setNotice(null);
    try {
      const res = await uploadDocument({
        file,
        title: title.trim(),
        description: description.trim() || undefined,
        sections,
        textOnly: effectiveTextOnly,
      });
      if (res.duplicate) {
        setNotice(intl.formatMessage({ id: "model.docs.duplicate", defaultMessage: "That document is already uploaded." }));
      } else {
        setFile(null);
        setTitle("");
        setDescription("");
        setSections([...SECTIONS]);
        setTextOnly(false);
      }
      await reload();
    } catch {
      setNotice(intl.formatMessage({ id: "model.docs.uploadError", defaultMessage: "Upload failed – try again." }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SettingsCard
      title={intl.formatMessage({ id: "model.docs.title", defaultMessage: "Knowledge documents" })}
      sub={intl.formatMessage({
        id: "model.docs.sub",
        defaultMessage: "Documents the assistant can search. Uploads are processed in the background.",
      })}
    >
      {/* Upload form */}
      <div className="space-y-3">
        <FileDropZone
          inputId="knowledge-upload"
          variant="zone"
          clickToOpen
          accept={RAG_DOCUMENT_ACCEPT}
          disabled={busy}
          onFiles={(files) => {
            const f = files.item(0) ?? null;
            setFile(f);
            if (f) setTitle(f.name.replace(/\.[^/.]+$/, "")); // prefill title from the filename, minus extension
          }}
          label={intl.formatMessage({ id: "model.docs.dropLabel", defaultMessage: "Drop a document or click to choose" })}
          hint={intl.formatMessage({ id: "model.docs.dropHint", defaultMessage: "PDF, Word, PowerPoint, and more, up to 50 MB" })}
        />
        {file ? <div className="smycles-field-meta text-ink-muted">{file.name}</div> : null}
        <input
          id="knowledge-title"
          className={INPUT}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={intl.formatMessage({ id: "model.docs.titlePlaceholder", defaultMessage: "Title (required)" })}
          aria-label={intl.formatMessage({ id: "model.docs.titleLabel", defaultMessage: "Document title" })}
        />
        <input
          id="knowledge-description"
          className={INPUT}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={intl.formatMessage({ id: "model.docs.descPlaceholder", defaultMessage: "What it's about (optional)" })}
          aria-label={intl.formatMessage({ id: "model.docs.descLabel", defaultMessage: "Document description" })}
        />
        <div className="flex flex-wrap gap-2">
          {SECTIONS.map((s) => (
            <FilterChip key={s} id={`knowledge-uploadsection-${s}`} active={sections.includes(s)} onClick={() => toggleUploadSection(s)} disabled={busy}>
              {SECTION_LABELS[s] ?? s}
            </FilterChip>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="knowledge-textonly"
            checked={effectiveTextOnly}
            onChange={setTextOnly}
            disabled={busy || !figuresSupported}
            ariaLabel={intl.formatMessage({ id: "model.docs.textOnly", defaultMessage: "Text only" })}
          />
          <span className="smycles-field-value">{intl.formatMessage({ id: "model.docs.textOnly", defaultMessage: "Text only" })}</span>
          <span id="knowledge-textonly-hint" className="smycles-field-meta text-ink-muted">
            {figuresSupported
              ? intl.formatMessage({ id: "model.docs.textOnlyHint", defaultMessage: "Skip images for faster processing; text stays searchable." })
              : intl.formatMessage({
                  id: "model.docs.textOnlyForced",
                  defaultMessage: "Images can't be extracted from this file type; it will be indexed as text.",
                })}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          {uploadHint ? (
            <span id="knowledge-uploadhint" className="smycles-field-meta text-ink-muted">
              {uploadHint}
            </span>
          ) : (
            <span />
          )}
          <Button id="knowledge-upload-submit" variant="primary" onClick={onUpload} disabled={!canUpload}>
            {uploadBlocked
              ? intl.formatMessage(
                  { id: "model.docs.uploadBlocked", defaultMessage: "Waiting for the provider switch to take effect… ({seconds}s)" },
                  { seconds: blockRemaining },
                )
              : intl.formatMessage(
                  { id: "model.docs.upload", defaultMessage: "Upload and process ({provider})" },
                  { provider: PROVIDER_LABELS[provider] ?? provider },
                )}
          </Button>
        </div>
      </div>

      {/* List — uploaded and global grouped under their own headers for a clear distinction. */}
      <div className="mt-6 space-y-6">
        {loadFailed ? (
          <div className="smycles-field-meta text-ink-muted">
            {intl.formatMessage({ id: "model.docs.loadError", defaultMessage: "Couldn't load documents." })}
          </div>
        ) : docs.length === 0 ? (
          <div id="knowledge-empty" className="smycles-field-meta text-ink-muted">
            {intl.formatMessage({ id: "model.docs.empty", defaultMessage: "No documents yet." })}
          </div>
        ) : (
          [
            { kind: "uploaded", header: "model.docs.sectionUploaded", fallback: "Uploaded documents" },
            { kind: "global", header: "model.docs.sectionGlobal", fallback: "Global documents" },
          ].map(({ kind, header, fallback }) => {
            const items = docs.filter((d) => d.kind === kind);
            if (items.length === 0) return null;
            return (
              <div key={kind} id={`knowledge-group-${kind}`} className="space-y-2">
                <div id={`knowledge-group-${kind}-title`} className="smycles-field-label text-ink-muted">
                  {intl.formatMessage({ id: header, defaultMessage: fallback })}
                </div>
                {items.map((doc) => (
                  <DocumentRow key={doc.id} doc={doc} provider={provider} blockRemaining={blockRemaining} intl={intl} onReload={reload} />
                ))}
              </div>
            );
          })
        )}
      </div>
    </SettingsCard>
  );
}

const PILL = "rounded px-1.5 py-0.5 text-smycles-micro font-semibold uppercase tracking-wide";

function StatusPill({ id, status, loading, unavailable, providerLabel, intl }: { id?: string; status: AdminDocument["status"]; loading: boolean; unavailable: boolean; providerLabel: string; intl: IntlShape }) {
  // Ready reads as available (accent); failed stays neutral (its reason shows as neutral errorDetail).
  // The spinner tracks `loading` (in progress AND within the timeout), so a shutdown-stalled doc stops it.
  // A ready doc lacking the active provider's embedding isn't searchable, so it reads as "New processing
  // required" in the danger tone — a deliberate, requested exception to "red = destructive button only"
  // (the sanctioned `text-danger` pending-destructive highlight; see index.css --color-danger).
  const spinner = loading ? (
    <span
      className="h-2.5 w-2.5 animate-spin rounded-full border border-ink-ui border-t-ink/60 motion-reduce:animate-none"
      aria-hidden
    />
  ) : null;
  if (unavailable) {
    return (
      <span
        id={id}
        className={`${PILL} inline-flex items-center gap-1 bg-danger/15 text-danger`}
        title={intl.formatMessage({
          id: "model.docs.unavailableHint",
          defaultMessage: "Not embedded for the current provider — re-embed to make it searchable.",
        })}
      >
        {spinner}
        {intl.formatMessage(
          { id: "model.docs.status.reembedRequired", defaultMessage: "New processing required ({provider})" },
          { provider: providerLabel },
        )}
      </span>
    );
  }
  const tone = status === "ready" ? "bg-accent/15 text-ink-accent" : "bg-ink/[0.06] text-ink-muted";
  const label =
    status === "processing"
      ? intl.formatMessage({ id: "model.docs.status.processingFor", defaultMessage: "Processing ({provider})" }, { provider: providerLabel })
      : intl.formatMessage({ id: `model.docs.status.${status}`, defaultMessage: status });
  return (
    <span id={id} className={`${PILL} inline-flex items-center gap-1 ${tone}`}>
      {spinner}
      {label}
    </span>
  );
}

function DocumentRow({ doc, provider, blockRemaining, intl, onReload }: { doc: AdminDocument; provider: string; blockRemaining: number; intl: IntlShape; onReload: () => Promise<void> }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const uploaded = doc.kind === "uploaded";
  const loading = isLoading(doc);
  const providers = doc.embeddedProviders ?? [];
  // Unavailable = ready but not embedded for the SELECTED provider (recomputes on tile switch).
  const unavailable = uploaded && doc.status === "ready" && !providers.includes(provider);
  const editableSections = uploaded && doc.status === "ready";
  // Same post-switch block as the upload button: don't reprocess until the provider switch has settled.
  const blocked = blockRemaining > 0;
  // The remove/cancel action, keyed by the doc's state:
  //  - "reprocess": pending/processing AND already has embeddings → an in-flight reprocess. "Cancel"
  //     reverts it to its prior version (keeps the doc, discards the new run) via cancelReprocess.
  //  - "upload": pending/processing with no embeddings yet → a first ingestion. "Cancel" discards it
  //     (delete) — there is no prior version to keep.
  //  - "delete": ready/failed → a plain "Delete" of the whole document.
  const inProgress = doc.status === "pending" || doc.status === "processing";
  const cancelKind: "reprocess" | "upload" | "delete" =
    !inProgress ? "delete" : providers.length > 0 ? "reprocess" : "upload";
  const keep = intl.formatMessage({ id: "model.docs.cancelKeep", defaultMessage: "Keep processing" });
  const dialogCopy = {
    reprocess: {
      title: intl.formatMessage({ id: "model.docs.cancelReprocessTitle", defaultMessage: "Stop reprocessing?" }),
      message: intl.formatMessage({ id: "model.docs.cancelReprocessMessage", defaultMessage: "“{title}” keeps its current version — the reprocessing in progress is discarded." }, { title: doc.title }),
      confirm: intl.formatMessage({ id: "model.docs.cancelReprocessConfirm", defaultMessage: "Stop reprocessing" }),
      dismiss: keep as string | undefined,
    },
    upload: {
      title: intl.formatMessage({ id: "model.docs.cancelTitle", defaultMessage: "Cancel processing?" }),
      message: intl.formatMessage({ id: "model.docs.cancelMessage", defaultMessage: "“{title}” will stop processing and be removed." }, { title: doc.title }),
      confirm: intl.formatMessage({ id: "model.docs.cancel", defaultMessage: "Cancel" }),
      dismiss: keep as string | undefined,
    },
    delete: {
      title: intl.formatMessage({ id: "model.docs.deleteTitle", defaultMessage: "Delete document?" }),
      message: intl.formatMessage({ id: "model.docs.deleteMessage", defaultMessage: "“{title}” and its extracted content will be removed." }, { title: doc.title }),
      confirm: intl.formatMessage({ id: "model.docs.delete", defaultMessage: "Delete" }),
      dismiss: undefined as string | undefined,
    },
  }[cancelKind];
  const providerLabel = PROVIDER_LABELS[provider] ?? provider;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
      await onReload();
    } catch {
      /* transient; the next poll/reload reflects real state */
    } finally {
      setBusy(false);
    }
  };

  const toggleSection = (s: string) => {
    const next = doc.sections.includes(s) ? doc.sections.filter((x) => x !== s) : [...doc.sections, s];
    if (next.length === 0) return; // at least one section (spec §7)
    void run(() => patchDocument(doc.id, { sections: next }));
  };

  return (
    <div id={`knowledge-doc-${doc.id}`} className="box flex items-start justify-between gap-4 p-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span id={`knowledge-doc-${doc.id}-title`} className="truncate text-smycles-body font-medium">
            {doc.title}
          </span>
          <StatusPill id={`knowledge-doc-${doc.id}-status`} status={doc.status} loading={loading || busy} unavailable={unavailable} providerLabel={providerLabel} intl={intl} />
          {providers.map((p) => (
            <span key={p} className={`${PILL} bg-ink/[0.06] text-ink-muted`} title={intl.formatMessage({ id: "model.docs.worksWith", defaultMessage: "Works with this provider" })}>
              {PROVIDER_LABELS[p] ?? p}
            </span>
          ))}
          {doc.textOnly ? (
            <span className={`${PILL} bg-ink/[0.06] text-ink-muted`}>
              {intl.formatMessage({ id: "model.docs.textOnly", defaultMessage: "Text only" })}
            </span>
          ) : null}
        </div>
        <div className="smycles-field-meta mt-0.5 truncate text-ink-muted">{doc.sourceName}</div>
        {uploaded ? (
          <div className="smycles-field-meta mt-0.5 text-ink-muted">
            {intl.formatMessage(
              { id: "model.docs.uploadedAt", defaultMessage: "Uploaded {date}" },
              { date: intl.formatDate(doc.createdAt, { dateStyle: "medium", timeStyle: "short" }) },
            )}
          </div>
        ) : null}
        {doc.status === "failed" ? (
          <div className="smycles-field-meta mt-1 text-ink-muted">
            {intl.formatMessage({
              id: "model.docs.failedHint",
              defaultMessage: "Processing failed. Try reprocessing, or re-upload the document.",
            })}
          </div>
        ) : null}
        <div className="mt-2 flex flex-wrap gap-1.5">
          {SECTIONS.map((s) =>
            editableSections ? (
              <FilterChip key={s} id={`knowledge-doc-${doc.id}-section-${s}`} active={doc.sections.includes(s)} onClick={() => toggleSection(s)} disabled={busy}>
                {SECTION_LABELS[s] ?? s}
              </FilterChip>
            ) : doc.sections.includes(s) ? (
              <span key={s} id={`knowledge-doc-${doc.id}-section-${s}`} className={`${PILL} bg-ink/[0.06] text-ink-muted`}>{SECTION_LABELS[s] ?? s}</span>
            ) : null,
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Switch
          id={`knowledge-doc-${doc.id}-enabled`}
          checked={!doc.disabled}
          onChange={(on) => void run(() => patchDocument(doc.id, { disabled: !on }))}
          disabled={busy}
          ariaLabel={intl.formatMessage({ id: "model.docs.enabledLabel", defaultMessage: "Enabled" })}
        />
        {uploaded ? (
          <>
            <Button id={`knowledge-doc-${doc.id}-download`} variant="secondary" size="sm" onClick={() => void downloadOriginal(doc.id, doc.sourceName)}>
              {intl.formatMessage({ id: "model.docs.download", defaultMessage: "Download" })}
            </Button>
            <Button
              id={`knowledge-doc-${doc.id}-reprocess`}
              variant="secondary"
              size="sm"
              disabled={busy || loading || blocked}
              title={blocked ? intl.formatMessage({ id: "model.docs.uploadBlocked", defaultMessage: "Waiting for the provider switch to take effect… ({seconds}s)" }, { seconds: blockRemaining }) : undefined}
              onClick={() => void run(() => reprocessDocument(doc.id, provider))}
            >
              {intl.formatMessage({ id: "model.docs.reprocess", defaultMessage: "Reprocess" })}
            </Button>
            <Button id={`knowledge-doc-${doc.id}-delete`} variant="destructive" size="sm" disabled={busy} onClick={() => setConfirmDelete(true)}>
              {cancelKind === "delete"
                ? intl.formatMessage({ id: "model.docs.delete", defaultMessage: "Delete" })
                : intl.formatMessage({ id: "model.docs.cancel", defaultMessage: "Cancel" })}
            </Button>
          </>
        ) : null}
      </div>

      <ConfirmDialog
        id={`knowledge-doc-${doc.id}-${cancelKind}-dialog`}
        open={confirmDelete}
        title={dialogCopy.title}
        message={dialogCopy.message}
        confirmLabel={dialogCopy.confirm}
        cancelLabel={dialogCopy.dismiss}
        onConfirm={() =>
          run(() => (cancelKind === "reprocess" ? cancelReprocess(doc.id) : deleteDocument(doc.id))).then(() =>
            setConfirmDelete(false),
          )
        }
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
